import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Download, ArrowLeft } from 'lucide-react';
import resumeAPI from '../services/api';
import TemplateSelector from '../components/templates/TemplateSelector';

const PublicResumePage = () => {
  const { publicLinkId } = useParams();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPdfExporting, setIsPdfExporting] = useState(false);

  useEffect(() => {
    loadResume();
  }, [publicLinkId]);

  const loadResume = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await resumeAPI.fetchByPublicLink(publicLinkId);
      if (data && data.length > 0) {
        setResume(data[0]);
      } else {
        setError('Resume not found');
      }
    } catch (err) {
      setError('Failed to load resume');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsPdfExporting(true);
      const url = `${window.location.origin}/resume-preview?public=${encodeURIComponent(publicLinkId)}&print=1`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      alert('Failed to download PDF');
      console.error(error);
    } finally {
      setTimeout(() => setIsPdfExporting(false), 500);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading resume...</p>
        </div>
      </div>
    );
  }

  if (error || !resume) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Resume Not Found</h1>
          <p className="text-gray-600 mb-6">
            The resume you're looking for doesn't exist or is no longer available.
          </p>
          <Link to="/" className="btn btn-primary inline-flex items-center space-x-2">
            <ArrowLeft className="w-5 h-5" />
            <span>Go Home</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{resume.title}</h1>
              {resume.headline && (
                <p className="text-gray-600 mt-1">{resume.headline}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="btn btn-outline inline-flex items-center space-x-2">
                <ArrowLeft className="w-5 h-5" />
                <span>Home</span>
              </Link>
              <button
                onClick={handleDownloadPDF}
                className="btn btn-primary inline-flex items-center space-x-2"
                disabled={isPdfExporting}
              >
                <Download className="w-5 h-5" />
                <span>{isPdfExporting ? 'Preparing PDF...' : 'Download PDF'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Resume Preview */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div id="resume-preview">
            <TemplateSelector template={resume.template} resume={resume} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicResumePage;
